import {
  useCreateRules,
  useGetRules,
  useValidationRules,
} from "@features/mx/readCycleManagement/hooks";
import { usePutValidationRule } from "@features/mx/validations/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { PageHeader } from "@shared/components/PageHeader";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { CircleCheck, Settings } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";

export const ValidationEstimationsTab = ({
  readCycleData,
}: {
  readCycleData: any;
}) => {
  console.log("readCycleData", readCycleData);
  const remoteUtilityId = useRemoteUtilityId();
  const { cycle_id } = useParams();
  const [openValidationRules, setOpenValidationRules] = useState(false);
  const [openEstimationRules, setOpenEstimationRules] = useState(false);
  const { data: validationRulesData } = useGetRules(
    0,
    openValidationRules,
    Number(cycle_id)
  );
  const { data: estimationRulesData } = useGetRules(
    1,
    openEstimationRules,
    Number(cycle_id)
  );

  const { mutate: updateValidationRule } = useCreateRules(cycle_id);

  const handleToggle = (ruleId: string, isActive: boolean) => {
    updateValidationRule({
      remote_utility_id: Number(remoteUtilityId),
      rule_id: ruleId,
      meter_cycle_id: Number(cycle_id),
      is_active: isActive,
    });
    logEvent("Validation Rule Updated", {
      id: ruleId,
      isActive: !isActive,
    });
  };
  return (
    <div>
      <PageHeader
        title="Validation Estimations"
        subtitle="View and manage validation and estimation rules for this read cycle"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <Card className="border shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <PageHeader
                title="Validation Rules"
                subtitle="Control how readings are validated"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpenValidationRules(true)}
                className="justify-start p-2"
              >
                <Settings className="h-4 w-4" />
                <span className="truncate text-left">Configure</span>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">
                Available Rules
              </span>
              <span className="text-lg font-semibold text-gray-900">
                {validationRulesData?.length}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <span className="text-sm font-medium text-blue-700">
                Applied Rules
              </span>
              <span className="text-lg font-semibold text-blue-900">
                {readCycleData?.validationRules?.length}
              </span>
            </div>
            <div className="mt-4 space-y-2">
              <p className="text-xs font-medium text-gray-700">
                Currently Applied:
              </p>
              {readCycleData?.validationRules?.length === 0 && (
                <p className="text-xs text-gray-500">No rules applied</p>
              )}
              <div className="grid grid-cols-2 gap-4">
                {readCycleData?.validationRules?.map((rule: any) => (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <CircleCheck className="h-4 w-4 text-green-600" />
                    {rule.name}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <PageHeader
                title="Estimation Rules"
                subtitle="Configure auto-completion logic"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setOpenEstimationRules(true)}
                className="justify-start p-2"
              >
                <Settings className="h-4 w-4" />
                <span className="truncate text-left">Configure</span>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-sm font-medium text-gray-700">
                Available Rules
              </span>
              <span className="text-lg font-semibold text-gray-900">
                {estimationRulesData?.length}
              </span>
            </div>
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <span className="text-sm font-medium text-blue-700">
                Applied Rules
              </span>
              <span className="text-lg font-semibold text-blue-900">
                {readCycleData?.estimationRules?.length}
              </span>
            </div>
            <div className="mt-4 space-y-2">
              <p className="text-xs font-medium text-gray-700">
                Currently Applied:
              </p>
              {readCycleData?.estimationRules?.length === 0 && (
                <p className="text-xs text-gray-500">No rules applied</p>
              )}
              <div className="grid grid-cols-2 gap-4">
                {readCycleData?.estimationRules?.map((rule: any) => (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <CircleCheck className="h-4 w-4 text-green-600" />
                    {rule.name}
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={openValidationRules} onOpenChange={setOpenValidationRules}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Validation Rules</DialogTitle>
            <DialogDescription>
              Enable or disable validation rules to control how readings are
              validated.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="flex items-center justify-between text-sm mb-4 pb-3 border-b">
              <span className="text-gray-600">
                Total Rules:{" "}
                <span className="font-semibold">
                  {validationRulesData?.length}
                </span>
              </span>
              <span className="text-gray-600">
                Applied Rules:{" "}
                <span className="font-semibold text-blue-600">
                  {validationRulesData?.filter((rule) => rule.isActive).length}
                </span>
              </span>
            </div>

            {/* Consumption Check */}
            {validationRulesData?.map((rule) => (
              <Card key={rule.name} className="border shadow-sm">
                <CardContent className="flex items-start justify-between p-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-base mb-1">
                      {rule.name}
                    </h3>
                    <p className="text-sm text-gray-600">{rule.description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={rule.isActive}
                      onChange={(e) => handleToggle(rule.id, e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setOpenValidationRules(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={openEstimationRules} onOpenChange={setOpenEstimationRules}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Estimation Rules</DialogTitle>
            <DialogDescription>
              Enable, disable and set priority for estimation logics.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="flex items-center justify-between text-sm mb-4 pb-3 border-b">
              <span className="text-gray-600">
                Total Rules:{" "}
                <span className="font-semibold">
                  {estimationRulesData?.length}
                </span>
              </span>
              <span className="text-gray-600">
                Applied Rules:{" "}
                <span className="font-semibold text-blue-600">
                  {estimationRulesData?.filter((rule) => rule.isActive).length}
                </span>
              </span>
            </div>

            <div className="rounded-md border border-blue-200 bg-blue-50 text-blue-800 px-4 py-3 text-sm mb-4">
              When you try to auto complete readings, the system will auto
              complete readings based on the priority set in the estimation
              rules below.
            </div>

            {/* Historical Average */}
            {estimationRulesData?.map((rule) => (
              <Card key={rule.name} className="border shadow-sm">
                <CardContent className="flex items-start justify-between p-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-base mb-1">
                      {rule.name}
                    </h3>
                    <p className="text-sm text-gray-600">{rule.description}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={rule.isActive}
                      onChange={(e) => handleToggle(rule.id, e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </CardContent>
              </Card>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setOpenEstimationRules(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
